import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";

const F_ICONS = ["Presentation", "FlaskConical", "Library", "Monitor", "BedDouble", "Bus", "Trophy", "Stethoscope"];
const P_ICONS = ["Ambulance", "HeartPulse", "Baby", "Users", "Microscope", "Syringe"];
const H_ICONS = ["Building2", "Stethoscope", "Brain", "Users", "HeartPulse"];

function StringListEditor({ label, items, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <Button variant="outline" size="sm" onClick={() => onChange([...items, ""])} className="rounded-full h-9"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input value={v} onChange={(e) => {
              const next = [...items]; next[i] = e.target.value; onChange(next);
            }} className="h-10 rounded-lg border-slate-200" />
            <button className="text-rose-500 p-2" onClick={() => onChange(items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminFacilities() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  if (!draft) return null;

  const f = draft.facilities || {};
  const p = draft.placements || {};
  const items = f.items || [];
  const exposure = p.exposureItems || [];

  const setItem = (i, k, v) => {
    const next = [...items]; next[i] = { ...next[i], [k]: v };
    patchPath("facilities.items", next);
  };

  const setExp = (i, k, v) => {
    const next = [...exposure]; next[i] = { ...next[i], [k]: v };
    patchPath("placements.exposureItems", next);
  };

  return (
    <div data-testid="cms-facilities-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Campus</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Facilities & Placements</h1>
        <p className="text-slate-500 mt-2">Edit facility cards, student life text, and placement/clinical exposure.</p>
      </div>

      <div className="space-y-6">
        {/* Facilities intro */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Facilities Page</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={f.eyebrow || ""} onChange={(e) => patchPath("facilities.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={f.heading || ""} onChange={(e) => patchPath("facilities.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro</Label>
              <Textarea value={f.intro || ""} onChange={(e) => patchPath("facilities.intro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
            <div><Label className="text-slate-700 text-sm">Student Life Heading</Label>
              <Input value={f.studentLifeHeading || ""} onChange={(e) => patchPath("facilities.studentLifeHeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Student Life Text</Label>
              <Textarea value={f.studentLifeText || ""} onChange={(e) => patchPath("facilities.studentLifeText", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
          </div>
        </div>

        {/* Facility cards */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Facility Cards</p>
            <Button variant="outline" size="sm" onClick={() => patchPath("facilities.items", [...items, { title: "New", icon: "Presentation", desc: "", image: "" }])} className="rounded-full h-9">
              <Plus className="w-4 h-4 mr-1.5" /> Add
            </Button>
          </div>
          <div className="space-y-4">
            {items.map((it, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading text-lg text-slate-900">{it.title}</h4>
                  <button className="text-rose-500 p-2" onClick={() => patchPath("facilities.items", items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Icon</Label>
                    <select value={it.icon || "Presentation"} onChange={(e) => setItem(i, "icon", e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-200 text-sm px-2 bg-white">
                      {F_ICONS.map((op) => (<option key={op} value={op}>{op}</option>))}
                    </select>
                  </div>
                  <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Title</Label>
                    <Input value={it.title || ""} onChange={(e) => setItem(i, "title", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                  </div>
                  <div className="sm:col-span-3"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</Label>
                    <Textarea value={it.desc || ""} onChange={(e) => setItem(i, "desc", e.target.value)} className="mt-1 rounded-lg border-slate-200 min-h-[70px]" />
                  </div>
                  <div className="sm:col-span-3">
                    <ImageUpload label="Image" value={it.image} onChange={(url) => setItem(i, "image", url)} folder="images" testid={`facility-image-${i}`} height={120} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Placements page */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Placements Page</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={p.eyebrow || ""} onChange={(e) => patchPath("placements.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={p.heading || ""} onChange={(e) => patchPath("placements.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro</Label>
              <Textarea value={p.intro || ""} onChange={(e) => patchPath("placements.intro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
            <div className="sm:col-span-2">
              <ImageUpload label="Header Image" value={p.image} onChange={(url) => patchPath("placements.image", url)} folder="images" testid="placements-image" />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Clinical Exposure</p>
              <Button variant="outline" size="sm" onClick={() => patchPath("placements.exposureItems", [...exposure, { title: "New", icon: "HeartPulse" }])} className="rounded-full h-9"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
            </div>
            <div className="space-y-2">
              {exposure.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 p-2">
                  <select value={it.icon || "HeartPulse"} onChange={(e) => setExp(i, "icon", e.target.value)} className="col-span-4 h-10 rounded-lg border border-slate-200 text-sm px-2 bg-white">
                    {P_ICONS.map((op) => (<option key={op} value={op}>{op}</option>))}
                  </select>
                  <Input value={it.title || ""} onChange={(e) => setExp(i, "title", e.target.value)} className="col-span-7 h-10 rounded-lg border-slate-200" />
                  <button className="text-rose-500 p-2" onClick={() => patchPath("placements.exposureItems", exposure.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5">
            <StringListEditor label="Placement Support Items" items={p.supportItems || []} onChange={(v) => patchPath("placements.supportItems", v)} />
          </div>
        </div>

        {/* Training Hospitals */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Training Hospitals (Placements Page)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Section Heading</Label>
              <Input value={p.hospitalsHeading || ""} onChange={(e) => patchPath("placements.hospitalsHeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Section Intro</Label>
              <Textarea value={p.hospitalsIntro || ""} onChange={(e) => patchPath("placements.hospitalsIntro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[70px]" />
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Hospitals</p>
            <Button variant="outline" size="sm" onClick={() => patchPath("placements.hospitals", [...(p.hospitals || []), { name: "New Hospital", location: "", tag: "", icon: "Building2", desc: "", image: "", skills: [] }])} className="rounded-full h-9">
              <Plus className="w-4 h-4 mr-1.5" /> Add
            </Button>
          </div>
          <div className="space-y-5">
            {(p.hospitals || []).map((h, i) => {
              const setH = (k, v) => {
                const next = [...(p.hospitals || [])];
                next[i] = { ...next[i], [k]: v };
                patchPath("placements.hospitals", next);
              };
              const setSkills = (arr) => setH("skills", arr);
              return (
                <div key={i} className="rounded-xl border border-slate-200 p-4 sm:p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading text-lg text-slate-900">{h.name || "Untitled Hospital"}</h4>
                    <button className="text-rose-500 p-2" onClick={() => patchPath("placements.hospitals", (p.hospitals || []).filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                    <div className="sm:col-span-3"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</Label>
                      <Input value={h.name || ""} onChange={(e) => setH("name", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                    </div>
                    <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Location / Type</Label>
                      <Input value={h.location || ""} onChange={(e) => setH("location", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                    </div>
                    <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Icon</Label>
                      <select value={h.icon || "Building2"} onChange={(e) => setH("icon", e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-200 text-sm px-2 bg-white">
                        {H_ICONS.map((op) => (<option key={op} value={op}>{op}</option>))}
                      </select>
                    </div>
                    <div className="sm:col-span-6"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tag (e.g. Multi-speciality)</Label>
                      <Input value={h.tag || ""} onChange={(e) => setH("tag", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                    </div>
                    <div className="sm:col-span-6"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</Label>
                      <Textarea value={h.desc || ""} onChange={(e) => setH("desc", e.target.value)} className="mt-1 rounded-lg border-slate-200 min-h-[110px]" />
                    </div>
                    <div className="sm:col-span-6">
                      <ImageUpload label="Hospital Image" value={h.image} onChange={(url) => setH("image", url)} folder="images" testid={`hospital-image-${i}`} height={140} />
                    </div>
                    <div className="sm:col-span-6">
                      <StringListEditor label="Key Learning Areas" items={h.skills || []} onChange={setSkills} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
